import { useEffect, useState } from "react";
import { useCollectionDataOnce } from "react-firebase-hooks/firestore";
import { Col, Container, Row } from "reactstrap";
import { firestore } from "./firebase";
import firebase from "firebase/app";
import festivalBgDesktop from "./img/festival/backgrounds/stimmex-festival-2026_sx desktop.png";
import festivalBgTablet from "./img/festival/backgrounds/stimmex-festival-2026_sx tablet.png";
import festivalBgMobile from "./img/festival/backgrounds/stimmex-festival-2026_sx mobile.png";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook, faInstagram } from "@fortawesome/free-brands-svg-icons";
import FestivalLogosBoard from "./component/FestivalLogosBoard";

export type FestivalEvent = {
  start: firebase.firestore.Timestamp | Date;
  title: string;
  programmText: string;
  teaser: string;
  description: string;
  picturePath: string;
  imageGallery?: string[];
  photoCopyright: string;
  id?: string;
};

const datesAreOnSameDay = (first: Date, second: Date) =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate();

const preSaturday = new Date("2026-06-20");
const preSunday = new Date("2026-06-21");
const thursday = new Date("2026-06-25");
const friday = new Date("2026-06-26");
const saturday = new Date("2026-06-27");
const sunday = new Date("2026-06-28");
const BG_TRANSITION_START = 100;
const BG_TRANSITION_DISTANCE = 450;

const getFestivalBgForViewport = (viewportWidth: number) => {
  if (viewportWidth <= 767) {
    return festivalBgMobile;
  }
  if (viewportWidth <= 992) {
    return festivalBgTablet;
  }
  return festivalBgDesktop;
};

const isWindowsPlatform = () => {
  if (typeof navigator === "undefined") {
    return false;
  }

  const navigatorWithUAData = navigator as Navigator & {
    userAgentData?: { platform?: string };
  };
  const platform =
    navigatorWithUAData.userAgentData?.platform ||
    navigator.platform ||
    navigator.userAgent;
  const normalizedPlatform = platform.toLowerCase();

  return (
    normalizedPlatform.startsWith("win") ||
    normalizedPlatform.includes("windows")
  );
};

const getDay = (date: Date) => {
  if (datesAreOnSameDay(date, preSaturday)) {
    return "preSaturday";
  }
  if (datesAreOnSameDay(date, preSunday)) {
    return "preSunday";
  }
  if (datesAreOnSameDay(date, thursday)) {
    return "thursday";
  }
  if (datesAreOnSameDay(date, friday)) {
    return "friday";
  }
  if (datesAreOnSameDay(date, saturday)) {
    return "saturday";
  }
  if (datesAreOnSameDay(date, sunday)) {
    return "sunday";
  }

  return "invalid";
};

const daySections = [
  { key: "preSaturday", label: "Samstag, 20.6." },
  { key: "preSunday", label: "Sonntag, 21.6." },
  { key: "thursday", label: "Donnerstag, 25.6." },
  { key: "friday", label: "Freitag, 26.6." },
  { key: "saturday", label: "Samstag, 27.6." },
  { key: "sunday", label: "Sonntag, 28.6." },
];

const festivalContentButtons = [
  {
    key: "about2026",
    route: "/fm/c/about2026",
    label: "About",
  },
  // {
  //   key: "artists2026",
  //   route: "/fm/c/artists2026",
  //   label: "Artists",
  // },
  {
    key: "cooperations2026",
    route: "/fm/c/cooperations2026",
    label: "Cooperations",
  },
  {
    key: "venues2026",
    route: "/fm/c/venues2026",
    label: "Venues",
  },
];

const Programm: React.FC<{ onclick: (e: any) => void }> = ({ onclick }) => {
  const history = useHistory();
  const isWindows = isWindowsPlatform();
  const [events] = useCollectionDataOnce<FestivalEvent>(
    firestore.collection("/festivalevents2026").orderBy("start", "asc")
  );
  const [eventsByDay, setEventsByDay] = useState<{
    [day: string]: FestivalEvent[];
  }>();
  const [bgTransitionProgress, setBgTransitionProgress] = useState(0);
  const [festivalIntroBgSrc, setFestivalIntroBgSrc] = useState(
    getFestivalBgForViewport(
      typeof window !== "undefined" ? window.innerWidth : 1200
    )
  );

  useEffect(() => {
    console.log("EVENTS", events);
    if (events && events.length > 0) {
      const eventsByMonth = events.reduce(
        (acc: { [day: string]: FestivalEvent[] }, event: FestivalEvent) => {
          if (event.start instanceof firebase.firestore.Timestamp) {
            event.start = event.start.toDate();
          }

          const day = getDay(event.start);
          if (acc[day]) {
            acc[day].push(event);
          } else {
            acc[day] = [event];
          }
          return acc;
        },
        {}
      );
      setEventsByDay(eventsByMonth);
    }
  }, [events]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const onResize = () => {
      setFestivalIntroBgSrc(getFestivalBgForViewport(window.innerWidth));
    };

    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const getEventElement = (events: FestivalEvent[]) =>
    events.map((e) => (
      <Col
        key={e.id || `${e.title}-${(e.start as Date).toISOString()}`}
        sm={12}
        style={{ cursor: "pointer" }}
        onClick={(ev) => {
          ev.stopPropagation();
          history.push(`/fm/${e.id}`);
        }}
      >
        {(e.start as Date).toLocaleTimeString("de-DE", {
          hour: "2-digit",
          minute: "2-digit",
        })}
        <div
          className="programm-element"
          dangerouslySetInnerHTML={{
            __html:
              e.programmText && e.programmText.trim().length > 0
                ? e.programmText
                : e.title,
          }}
        ></div>
        <br />
      </Col>
    ));

  const renderDayEvents = (dayKey: string) => {
    const dayEvents = eventsByDay && eventsByDay[dayKey];
    if (dayEvents && dayEvents.length > 0) {
      return getEventElement(dayEvents);
    }

    return (
      <Col sm={12}>
        <p className="programm-day-empty">Noch keine Programmpunkte.</p>
      </Col>
    );
  };
  const onContentScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
    const container = event.currentTarget;
    const rawProgress = Math.min(
      1,
      Math.max(
        0,
        (container.scrollTop - BG_TRANSITION_START) / BG_TRANSITION_DISTANCE
      )
    );
    const progress = rawProgress <= 0.001 ? 0 : rawProgress >= 0.999 ? 1 : rawProgress;
    setBgTransitionProgress(progress);
  };
  const bgOpacityProgress = 1 - Math.pow(1 - bgTransitionProgress, 3);

  console.log({ festivalBgMobile, festivalBgTablet, festivalBgDesktop });

  return (
    <div
      className={`programm${isWindows ? " programm-windows-scrollbar" : ""}`}
      onClick={onclick}
      onScroll={onContentScroll}
    >
      <picture className="festival-intro-bg">
        <img src={festivalIntroBgSrc} alt="" />
      </picture>

      {/* <div
        className="festival-static-bg"
        style={
          {
            "--festival-bg-transition-progress": bgTransitionProgress,
            "--festival-bg-opacity": bgOpacityProgress,
          } as React.CSSProperties
        }
      >

      </div> */}
      <Container className="programm-container" fluid>

        <Row >
          <Col sm={12}>
            <div className="festival-content-buttons">
              {festivalContentButtons.map((button) => (
                <button
                  key={button.key}
                  type="button"
                  className="bubble festival-content-bubble"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    history.push(button.route);
                  }}
                >
                  {button.label}
                </button>
              ))}
            </div>
          </Col>

          <Col sm={12}>
            <h2>Festivalprogramm</h2>
            <br />
            <br />
          </Col>

          <Col sm={12} className="mobile-only">
            <div className="programm-mobile-accordion">
              {daySections.map((section, index) => (
                <details
                  key={section.key}
                  className="programm-day-accordion"
                  open={index === 0}
                >
                  <summary>{section.label}</summary>
                  <div className="programm-day-accordion-content">
                    {renderDayEvents(section.key)}
                  </div>
                </details>
              ))}
            </div>
          </Col>

          <Col lg={6} className="programm-day-col desktop-only">
            <Col sm={12}>
              <h5>Samstag, 20.6.</h5>
              <hr />
            </Col>
            {renderDayEvents("preSaturday")}
          </Col>
          <Col lg={6} className="programm-day-col desktop-only">
            <Col sm={12}>
              <h5>Sonntag, 21.6.</h5>
              <hr />
            </Col>
            {renderDayEvents("preSunday")}
          </Col>

          <Col lg={3} className="programm-day-col desktop-only">
            <Col sm={12}>
              <h5>Donnerstag, 25.6.</h5>
              <hr />
            </Col>
            {renderDayEvents("thursday")}
          </Col>
          <Col lg={3} className="programm-day-col desktop-only">
            <Col sm={12}>
              <h5>Freitag, 26.6.</h5>
              <hr />
            </Col>
            {renderDayEvents("friday")}
          </Col>
          <Col lg={3} className="programm-day-col desktop-only">
            <Col sm={12}>
              <h5>Samstag, 27.6.</h5>
              <hr />
            </Col>
            {renderDayEvents("saturday")}
          </Col>

          <Col lg={3} className="programm-day-col desktop-only">
            <Col sm={12}>
              <h5>Sonntag, 28.6.</h5>
              <hr />
            </Col>
            {renderDayEvents("sunday")}
          </Col> 
        </Row>
         <Row>
          <Col sm={12}>
            <hr />
            <FontAwesomeIcon
              icon={faFacebook}
              onClick={() => window.open("https://www.facebook.com/StimmeX")}
              className="fb-icon-mobile"
              style={{
                height: "3vh",
                width: "auto",
                zIndex: 1,
                color: "#9700ff",
                cursor: "pointer",
              }}
            />
            <FontAwesomeIcon
              icon={faInstagram}
              onClick={() => window.open("https://www.instagram.com/stimme_x/")}
              className="insta-icon-mobile"
              style={{
                marginLeft: "1rem",
                height: "3vh",
                width: "auto",
                zIndex: 1,
                color: "#9700ff",
                cursor: "pointer",
              }}
            />
          </Col>
          
          <Col sm={12}>Gefördert durch:</Col>
          <Col sm={12}>
            <br />
          </Col>
          <Col sm={12}>
            <FestivalLogosBoard />
          </Col>

          <Col sm={12}>
            <br />
            <br />

          </Col>
        </Row> 
        
      </Container>
    </div>
  );
};

export default Programm;
